/**
 * @file Components/Sections/Hero.jsx
 * @description Premium hero with parallax background image, animated
 * typewriter text, icon nav, search bar with popular routes, and trust badges.
 */

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plane, Bus, TrainFront, Hotel, BookOpen, Globe, Compass, ArrowRight, Search, Users, MapPin, Shield } from "lucide-react";
import { useSiteSettings } from "../../Context/SiteSettingsContext";
import AnimatedButton from "../Ui/AnimatedButton";

const quickNav = [
  { icon: Plane, label: 'Flights', to: '/search?tab=flights' },
  { icon: Hotel, label: 'Hotels', to: '/hotels' },
  { icon: Bus, label: 'Buses', to: '/search?tab=buses' },
  { icon: TrainFront, label: 'Trains', to: '/search?tab=trains' },
  { icon: Compass, label: 'Tours', to: '/tours' },
  { icon: Globe, label: 'Outbound', to: '/outbound' },
];

const ROTATING_WORDS = ['Kashmir', 'Goa', 'Rajasthan', 'Kerala', 'Ladakh', 'Manali', 'Europe', 'Dubai'];
const POPULAR = ['Delhi → Manali', 'Mumbai → Goa', 'Delhi → Jaipur', 'Bangalore → Ooty'];

const Hero = () => {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const hero = settings?.hero || {};
  const [scrollY, setScrollY] = useState(0);
  const [searchVal, setSearchVal] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [activeNav, setActiveNav] = useState(0);

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setWordIndex(i => (i + 1) % ROTATING_WORDS.length), 2500);
    return () => clearInterval(id);
  }, []);

  const handleSearch = () => {
    if (searchVal.trim()) navigate(`/tours?search=${encodeURIComponent(searchVal.trim())}`);
    else navigate('/tours');
  };

  return (
    <section className="relative min-h-[420px] sm:min-h-[480px] md:min-h-[600px] flex flex-col justify-end overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage: `url('${hero.backgroundImage || '/ai-taj.webp'}')`,
          transform: `translateY(${scrollY * 0.2}px) scale(1.08)`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#070b24] via-[#070b24]/60 to-[#070b24]/20" />

      {/* Floating Trust Badges (over image, hidden on mobile to prevent overlap) */}
      <div className="absolute top-28 md:top-36 left-0 right-0 z-10 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4 md:gap-8 flex-wrap">
            {[
              { icon: Users, text: '15,000+ Travellers' },
              { icon: MapPin, text: '250+ Destinations' },
              { icon: Shield, text: 'Best Price Guarantee' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2">
                <b.icon size={14} className="text-white/70" />
                <span className="text-white/80 text-xs font-semibold">{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 pb-0">
        {/* Animated Heading */}
        <div className="text-center mb-8">
          <p className="text-white/50 text-sm font-medium mb-3 tracking-wider uppercase">India's Most Trusted Travel Company</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] tracking-tight">
            Explore{' '}
            <span className="relative inline-block">
              <span key={wordIndex} className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400 animate-pulse">
                {ROTATING_WORDS[wordIndex]}
              </span>
            </span>
          </h1>
          <p className="text-white/50 text-xs sm:text-sm md:text-base mt-3 md:mt-4 max-w-lg mx-auto px-4">
            {hero.subtitle || 'Discover extraordinary destinations, create unforgettable memories, and experience the journey of a lifetime'}
          </p>
        </div>

        {/* ─── Booking Widget ─── */}
        <div className="bg-white rounded-t-2xl shadow-[0_-10px_60px_rgba(0,0,0,0.3)] max-w-5xl mx-2 sm:mx-auto overflow-hidden">
          {/* Icon Nav */}
          <div className="flex overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {quickNav.map((item, idx) => {
              const Icon = item.icon;
              const active = idx === activeNav;
              return (
                <button
                  key={item.label}
                  onClick={() => { setActiveNav(idx); navigate(item.to); }}
                  className={`group flex-1 min-w-[60px] flex flex-col items-center py-3 md:py-5 relative transition-all ${
                    active ? 'bg-[#243185]/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} strokeWidth={2} className={`mb-1 transition-colors ${active ? 'text-[#243185]' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  <span className={`text-[10px] md:text-xs font-semibold transition-colors whitespace-nowrap ${active ? 'text-[#243185]' : 'text-gray-500 group-hover:text-gray-700'}`}>
                    {item.label}
                  </span>
                  {active && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 md:w-10 h-[3px] bg-[#243185] rounded-full" />}
                </button>
              );
            })}
          </div>

          {/* Search Section */}
          <div className="border-t border-gray-100 p-3 sm:p-4 md:p-5">
            <div className="flex items-center gap-2 sm:gap-3 bg-gray-50 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 focus-within:border-[#243185] focus-within:ring-2 focus-within:ring-[#243185]/10 transition-all">
              <Search size={18} className="text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                placeholder="Where do you want to go?"
                className="flex-1 min-w-0 text-sm md:text-base text-gray-800 placeholder:text-gray-400 outline-none bg-transparent font-medium"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <AnimatedButton onClick={handleSearch} variant="primary" size="sm" className="!px-3 sm:!px-6 !text-xs sm:!text-sm" icon={ArrowRight} iconPosition="right">
                <span className="hidden sm:inline">Search</span>
                <span className="sm:hidden">Go</span>
              </AnimatedButton>
            </div>

            {/* Popular Routes */}
            <div className="flex items-center gap-2 mt-2 sm:mt-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <span className="text-[11px] text-gray-400 font-medium whitespace-nowrap">Popular:</span>
              {POPULAR.map((route, i) => (
                <button key={i} onClick={() => {
                  const [from] = route.split(' → ');
                  setSearchVal(from);
                  navigate(`/tours?search=${encodeURIComponent(from)}`);
                }}
                  className="text-[11px] px-3 py-1.5 bg-gray-100 hover:bg-[#243185]/5 hover:text-[#243185] text-gray-500 rounded-full whitespace-nowrap font-medium transition-colors border border-transparent hover:border-[#243185]/20"
                >
                  {route}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
